import { ChangeDetectionStrategy, Component } from '@angular/core';
import { UntilDestroy } from '@ngneat/until-destroy';
import { BaseCellRendererComponent, Params } from '@y42/ag-grid';
import { connectState } from '@y42/angular/util';
import { Y42FeatureType } from '@y42/json-schema';
import { filter, map, shareReplay } from 'rxjs';
import { FeatureAggregationService } from '../../../api';
import { AbstractFeatureUIModel } from '../../../models';

export interface EntityTypeCellRendererParams {
  hideChildIcon?: boolean;
}

@UntilDestroy()
@Component({
  selector: 'y42-entity-type-cell-renderer',
  templateUrl: './entity-type-cell-renderer.component.html',
  styleUrls: ['./entity-type-cell-renderer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EntityTypeCellRendererComponent extends BaseCellRendererComponent<
  AbstractFeatureUIModel['typeLabel'],
  AbstractFeatureUIModel,
  Params<AbstractFeatureUIModel['typeLabel'], AbstractFeatureUIModel, EntityTypeCellRendererParams>
> {
  constructor(private readonly featureAggregationService: FeatureAggregationService) {
    super();
  }

  private readonly type$ = this.params$.pipe(
    filter(Boolean),
    map((params) => {
      const data = params.node.data || (params.node.group ? params.node.allLeafChildren?.[0]?.data : null);

      let typeLabel: string | null | undefined;
      let typeIcon: string | null | undefined;

      if (data?.typeIcon) {
        typeLabel = data?.typeLabel;
        typeIcon = data?.typeIcon;
      } else if (data?.featureType) {
        typeLabel = data.featureType;
        typeIcon = this.featureAggregationService.getLogoByFeatureType(data.featureType as Y42FeatureType) || undefined;
      }
      return { typeLabel, typeIcon };
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  readonly state = connectState(this, {
    // Always show the icon when the ag-grid is flat (no row grouping)
    // otherwise show the icon only on the parent row (datasource) and not on children (tables)
    showIcon: this.params$.pipe(
      map((params) => {
        const hideChildIcon = !!params?.hideChildIcon;
        const group = !!params?.node.group;

        return group || !hideChildIcon;
      }),
    ),
    typeLabel: this.type$.pipe(map((type) => type.typeLabel)),
    typeIcon: this.type$.pipe(map((type) => type.typeIcon)),
  });
}
